import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ScrollView, View, StyleSheet, NativeSyntheticEvent, NativeScrollEvent, StyleProp, ViewStyle } from 'react-native';

import Text from '../global/Text';
import fontMaker from '../../utils/font';

type QuestionnaireScrollPickerProps = {
  dataSource: Array<string | number>;
  selectedIndex: number;
  onValueChange: (value: string | number, index: number) => void;
  itemHeight?: number;
  /** Number of visible rows (odd number so one sits in the center). */
  visibleCount?: number;
  style?: StyleProp<ViewStyle>;
};

export type QuestionnaireScrollPickerHandle = {
  scrollToIndex: (index: number) => void;
};

/**
 * A lightweight scroll-wheel/spinner picker built on ScrollView so it works on
 * React Native Web with both touch drag and mouse wheel. Selection is driven by
 * the scroll offset and snapped after scrolling settles. It also exposes an
 * imperative `scrollToIndex` so external controls (e.g. arrow buttons) can move it.
 */
const QuestionnaireScrollPicker = React.forwardRef<QuestionnaireScrollPickerHandle, QuestionnaireScrollPickerProps>(
  (props, ref) => {
    const itemHeight = props.itemHeight ?? 50;
    const visibleCount = props.visibleCount ?? 3;
    const wrapperHeight = itemHeight * visibleCount;
    const padCount = Math.floor(visibleCount / 2);

    const scrollRef = useRef<ScrollView>(null);
    const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastNotified = useRef<number>(props.selectedIndex);
    const [activeIndex, setActiveIndex] = useState<number>(props.selectedIndex >= 0 ? props.selectedIndex : 0);

    // Set the initial scroll position to the selected item.
    useEffect(() => {
      const initialIndex = props.selectedIndex >= 0 ? props.selectedIndex : 0;
      const id = setTimeout(() => scrollRef.current?.scrollTo({ y: initialIndex * itemHeight, animated: false }), 0);
      return () => clearTimeout(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(
      () => () => {
        if (settleTimer.current) {
          clearTimeout(settleTimer.current);
        }
      },
      []
    );

    useImperativeHandle(ref, () => ({
      scrollToIndex: (index: number) => {
        const clamped = Math.max(0, Math.min(props.dataSource.length - 1, index));
        // Mark as already notified so the resulting scroll events don't re-fire onValueChange.
        lastNotified.current = clamped;
        setActiveIndex(clamped);
        scrollRef.current?.scrollTo({ y: clamped * itemHeight, animated: true });
      }
    }));

    const handleScroll = useCallback(
      (e: NativeSyntheticEvent<NativeScrollEvent>) => {
        const y = e.nativeEvent.contentOffset.y;
        const index = Math.round(y / itemHeight);

        // Update highlight in real time as the wheel moves.
        if (index >= 0 && index < props.dataSource.length) {
          setActiveIndex(index);
        }

        // Snap + notify once scrolling has settled.
        if (settleTimer.current) {
          clearTimeout(settleTimer.current);
        }
        settleTimer.current = setTimeout(() => {
          const settledIndex = Math.max(0, Math.min(props.dataSource.length - 1, Math.round(y / itemHeight)));
          const targetY = settledIndex * itemHeight;
          if (Math.abs(targetY - y) > 0.5) {
            scrollRef.current?.scrollTo({ y: targetY, animated: true });
          }
          if (settledIndex !== lastNotified.current) {
            lastNotified.current = settledIndex;
            props.onValueChange(props.dataSource[settledIndex], settledIndex);
          }
        }, 120);
      },
      [itemHeight, props]
    );

    return (
      <View style={[{ height: wrapperHeight, overflow: 'hidden' }, props.style]}>
        <ScrollView
          ref={scrollRef}
          bounces={false}
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled
          scrollEventThrottle={16}
          onScroll={handleScroll}
        >
          <View style={{ height: padCount * itemHeight }} />
          {props.dataSource.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <View key={index} style={[styles.item, { height: itemHeight }]}>
              <Text style={index === activeIndex ? styles.activeText : styles.inactiveText}>{item}</Text>
            </View>
          ))}
          <View style={{ height: padCount * itemHeight }} />
        </ScrollView>
      </View>
    );
  }
);

export default QuestionnaireScrollPicker;

const styles = StyleSheet.create({
  item: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  activeText: {
    fontSize: 24,
    color: '#333',
    ...fontMaker({ weight: 'SemiBold' })
  },
  inactiveText: {
    fontSize: 18,
    color: '#b8b8b8'
  }
});
